import { useState } from "react";
import { toast } from "sonner";
import { UserPlus, Check, Lock, CreditCard } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";

const empty = { first_name: "", last_name: "", email: "", partner_first_name: "", partner_last_name: "", partner_email: "" };

export const RegisterDialog = ({ tournamentId, tournamentName, onRegistered, registrationOpen = true, deadline, startTime = "20:00", isDoubles = false, paymentLink, entryFee = 6 }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (isDoubles && (!form.partner_first_name.trim() || !form.partner_last_name.trim())) {
      toast.error("Vul de gegevens van beide spelers in.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        ...(isDoubles
          ? {
              partner_first_name: form.partner_first_name,
              partner_last_name: form.partner_last_name,
              partner_email: form.partner_email || null,
            }
          : {}),
      };
      await api.post(`/tournaments/${tournamentId}/register`, payload);
      setDone(true);
      toast.success("Inschrijving gelukt! Tot bij Anni's Place.");
      onRegistered?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const close = (v) => {
    setOpen(v);
    if (!v) {
      setForm(empty);
      setDone(false);
    }
  };

  const feeStr = String(entryFee).replace(".", ",");

  const inputCls =
    "w-full border-b-2 border-[#E5E5E5] bg-transparent py-2.5 px-1 text-[#0A0A0A] focus:outline-none focus:border-[#D4AF37] transition-colors";
  const labelCls = "text-xs uppercase tracking-[0.15em] font-semibold text-[#525252]";

  if (!registrationOpen) {
    const dl = deadline ? new Date(deadline) : null;
    return (
      <div
        data-testid="registration-closed"
        className="inline-flex items-center gap-2 bg-[#F2F0EA] text-[#525252] px-5 py-2.5 font-semibold border border-[#E5E5E5] cursor-not-allowed"
        title={dl ? `Inschrijvingen gesloten sinds ${dl.toLocaleString("nl-NL")}` : "Inschrijvingen gesloten"}
      >
        <Lock className="w-4 h-4" /> Inschrijvingen gesloten
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <button
        onClick={() => setOpen(true)}
        data-testid="open-register-btn"
        className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0A] px-5 py-2.5 font-bold hover:bg-[#A8882D] transition-colors"
      >
        <UserPlus className="w-4 h-4" /> Inschrijven
      </button>

      <DialogContent className="rounded-none border-2 border-[#0A0A0A] sm:max-w-md" data-testid="register-dialog">
        {done ? (
          <div className="text-center py-6">
            <div className="inline-flex w-14 h-14 bg-[#16A34A] items-center justify-center mb-4 rounded-full">
              <Check className="w-7 h-7 text-white" />
            </div>
            <h3 className="font-heading text-2xl font-bold mb-2">Je bent ingeschreven!</h3>
            <p className="text-[#525252] text-sm">
              Bedankt voor je inschrijving voor <strong>{tournamentName}</strong>. Aanvang {startTime} — inschrijving €{feeStr}.
            </p>
            {paymentLink && (
              <a
                href={paymentLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pay-now-btn"
                className="mt-6 inline-flex items-center justify-center gap-2 w-full bg-[#D4AF37] text-[#0A0A0A] px-6 py-3 font-bold hover:bg-[#A8882D] transition-colors"
              >
                <CreditCard className="w-4 h-4" /> Betaal nu €{feeStr}
              </a>
            )}
            <button
              onClick={() => close(false)}
              data-testid="register-close-btn"
              className="mt-3 bg-[#0A0A0A] text-[#D4AF37] px-6 py-2.5 font-semibold hover:bg-[#1A1A1A] transition-colors"
            >
              Sluiten
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl font-black tracking-tight">
                Inschrijven — {tournamentName}
              </DialogTitle>
              <DialogDescription className="text-[#525252]">
                {isDoubles
                  ? "Koppeltoernooi — vul de gegevens van beide spelers in (minimaal 1 e-mailadres)."
                  : "Vul je gegevens in om je in te schrijven voor dit toernooi."}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-5 mt-2 max-h-[60vh] overflow-y-auto pr-1" data-testid="register-form">
              {isDoubles && <p className="text-xs font-bold uppercase tracking-wider text-[#A8882D]">Speler 1</p>}
              <div>
                <label className={labelCls}>Naam</label>
                <input
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  data-testid="register-first-name"
                  required
                  className={inputCls}
                  placeholder="Voornaam"
                />
              </div>
              <div>
                <label className={labelCls}>Achternaam</label>
                <input
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  data-testid="register-last-name"
                  required
                  className={inputCls}
                  placeholder="Achternaam"
                />
              </div>
              <div>
                <label className={labelCls}>E-mail</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="register-email"
                  required
                  className={inputCls}
                  placeholder="naam@voorbeeld.nl"
                />
              </div>

              {isDoubles && (
                <>
                  <p className="text-xs font-bold uppercase tracking-wider text-[#A8882D] pt-2 border-t border-[#E5E5E5]">Speler 2</p>
                  <div>
                    <label className={labelCls}>Naam</label>
                    <input
                      value={form.partner_first_name}
                      onChange={(e) => setForm({ ...form, partner_first_name: e.target.value })}
                      data-testid="register-partner-first-name"
                      required
                      className={inputCls}
                      placeholder="Voornaam partner"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Achternaam</label>
                    <input
                      value={form.partner_last_name}
                      onChange={(e) => setForm({ ...form, partner_last_name: e.target.value })}
                      data-testid="register-partner-last-name"
                      required
                      className={inputCls}
                      placeholder="Achternaam partner"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>E-mail partner (optioneel)</label>
                    <input
                      type="email"
                      value={form.partner_email}
                      onChange={(e) => setForm({ ...form, partner_email: e.target.value })}
                      data-testid="register-partner-email"
                      className={inputCls}
                      placeholder="partner@voorbeeld.nl"
                    />
                  </div>
                </>
              )}
              <DialogFooter>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="register-submit-btn"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#0A0A0A] text-[#D4AF37] py-3 font-semibold hover:bg-[#1A1A1A] transition-colors disabled:opacity-60"
                >
                  <UserPlus className="w-4 h-4" /> {loading ? "Bezig…" : "Inschrijving voltooien"}
                </button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
